/**
 * Part-to-whole data preparation for the donut and pie chart.
 *
 * Lives beside the chart rather than in the shared chartUtils module because
 * every rule here is specific to a whole made of shares: negatives cannot be
 * drawn, duplicates must merge before shares are computed, and a long tail has
 * to fold rather than grow new hues.
 */

import { isValidColor } from "../chartUtils";

export interface ChartSlice {
  /** Stable identity for React keys and colour assignment. */
  id: string;
  label: string;
  value: number;
  /** Fraction of total, 0..1. Zero when the total is zero. */
  share: number;
  color: string;
  /** True for the synthetic bucket that folds the long tail. */
  isOther: boolean;
  /** Labels folded into this bucket, present only when isOther. */
  members?: string[];
  /** The caller's original row, handed back to onSliceClick. */
  source?: unknown;
}

export interface NormalizedChartData {
  slices: ChartSlice[];
  total: number;
  /** Rows dropped as unusable (non-object, non-finite, or negative value). */
  skipped: number;
  /** Human readable notes about what was dropped or folded. */
  warnings: string[];
}

export type SliceSort = "desc" | "asc" | "none";

export interface NormalizeOptions {
  nameKey: string;
  valueKey: string;
  colorKey: string;
  palette: readonly string[];
  otherColor: string;
  maxSlices: number;
  otherLabel: string;
  sort: SliceSort;
}

/**
 * Turn arbitrary caller data into slices that are always safe to render.
 *
 * Drops rows that cannot be plotted in a part-to-whole chart (non-finite and
 * negative values), merges duplicate labels, sorts, then folds the tail beyond
 * maxSlices into a single neutral "Other" bucket so hues are never cycled.
 * Colour is assigned by entity before any folding, so hiding a slice from the
 * legend never repaints the survivors.
 */
export function normalizeChartData(
  data: unknown,
  options: NormalizeOptions,
): NormalizedChartData {
  const { nameKey, valueKey, colorKey, palette, otherColor, maxSlices, otherLabel, sort } =
    options;

  const warnings: string[] = [];

  if (data == null) {
    return { slices: [], total: 0, skipped: 0, warnings };
  }

  if (!Array.isArray(data)) {
    return {
      slices: [],
      total: 0,
      skipped: 0,
      warnings: ["Chart data must be an array."],
    };
  }

  // Merge duplicate labels so two rows named "Open" become one slice.
  const merged = new Map<string, { label: string; value: number; color?: string; source: unknown }>();
  let skipped = 0;
  let negatives = 0;
  let invalids = 0;

  data.forEach((row, index) => {
    if (row == null || typeof row !== "object") {
      skipped += 1;
      invalids += 1;
      return;
    }

    const record = row as Record<string, unknown>;
    const rawLabel = record?.[nameKey];
    const label =
      typeof rawLabel === "string" || typeof rawLabel === "number"
        ? String(rawLabel).trim()
        : "";
    const safeLabel = label || `Item ${index + 1}`;

    const rawValue = record?.[valueKey];
    const value = typeof rawValue === "number" ? rawValue : Number(rawValue);

    if (!Number.isFinite(value)) {
      skipped += 1;
      invalids += 1;
      return;
    }
    if (value < 0) {
      skipped += 1;
      negatives += 1;
      return;
    }

    const existing = merged.get(safeLabel);
    if (existing) {
      existing.value += value;
      return;
    }

    const rawColor = record?.[colorKey];
    merged.set(safeLabel, {
      label: safeLabel,
      value,
      color: isValidColor(rawColor) ? rawColor : undefined,
      source: row,
    });
  });

  if (invalids > 0) {
    warnings.push(
      `${invalids} ${invalids === 1 ? "entry was" : "entries were"} skipped because the value was missing or not a number.`,
    );
  }
  if (negatives > 0) {
    warnings.push(
      `${negatives} negative ${negatives === 1 ? "value was" : "values were"} skipped. A part-to-whole chart cannot show negative values.`,
    );
  }

  let entries = Array.from(merged.values());

  if (sort === "desc") entries.sort((a, b) => b.value - a.value);
  else if (sort === "asc") entries.sort((a, b) => a.value - b.value);

  // Assign colour by entity, before folding, so the palette slot for a given
  // label does not shift when other slices are hidden.
  const colored: ChartSlice[] = entries.map((entry, i) => ({
    id: entry.label,
    label: entry.label,
    value: entry.value,
    share: 0,
    color: entry.color ?? palette[i % palette.length] ?? otherColor,
    isOther: false,
    source: entry.source,
  }));

  let slices = colored;

  // Past the cap, extra hues stop being distinguishable. Fold the tail rather
  // than generating a ninth colour.
  const cap = Math.max(1, Math.floor(maxSlices));
  if (colored.length > cap) {
    const head = colored.slice(0, cap - 1);
    const tail = colored.slice(cap - 1);
    const tailTotal = tail.reduce((sum, s) => sum + s.value, 0);
    head.push({
      id: "__other__",
      label: otherLabel,
      value: tailTotal,
      share: 0,
      color: otherColor,
      isOther: true,
      members: tail.map((s) => s.label),
    });
    slices = head;
    warnings.push(
      `${tail.length} smaller categories were grouped into "${otherLabel}".`,
    );
  }

  const total = slices.reduce((sum, s) => sum + s.value, 0);
  const withShare = slices.map((s) => ({
    ...s,
    share: total > 0 ? s.value / total : 0,
  }));

  return { slices: withShare, total, skipped, warnings };
}
